import {
  evaluateCyanOwnerAuthority,
  type CyanOwnerAuthorityInput,
  type CyanOwnerAuthorityResult,
} from "./cyanOwnerAuthority";

import type {
  CyanOwnerDecisionRecord,
  CyanOwnerDecisionRepository,
} from "./cyanOwnerDecisionRepository";

function cyanSha256(value: string): string {
  const rotateRight = (valueToRotate: number, amount: number): number =>
    (valueToRotate >>> amount) | (valueToRotate << (32 - amount));

  const constants = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
    0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
    0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
    0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
    0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
    0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];

  const initialHash = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
    0x1f83d9ab, 0x5be0cd19,
  ];

  const bytes = Array.from(new TextEncoder().encode(value));

  const bitLength = bytes.length * 8;

  bytes.push(0x80);

  while (bytes.length % 64 !== 56) {
    bytes.push(0);
  }

  const high = Math.floor(bitLength / 0x100000000);

  const low = bitLength >>> 0;

  for (let shift = 24; shift >= 0; shift -= 8) {
    bytes.push((high >>> shift) & 0xff);
  }

  for (let shift = 24; shift >= 0; shift -= 8) {
    bytes.push((low >>> shift) & 0xff);
  }

  const hash = [...initialHash];

  for (let offset = 0; offset < bytes.length; offset += 64) {
    const words = new Array<number>(64).fill(0);

    for (let index = 0; index < 16; index += 1) {
      const position = offset + index * 4;

      words[index] =
        ((bytes[position] << 24) |
          (bytes[position + 1] << 16) |
          (bytes[position + 2] << 8) |
          bytes[position + 3]) >>>
        0;
    }

    for (let index = 16; index < 64; index += 1) {
      const word15 = words[index - 15];
      const word2 = words[index - 2];

      const sigma0 =
        rotateRight(word15, 7) ^ rotateRight(word15, 18) ^ (word15 >>> 3);

      const sigma1 =
        rotateRight(word2, 17) ^ rotateRight(word2, 19) ^ (word2 >>> 10);

      words[index] =
        (words[index - 16] + sigma0 + words[index - 7] + sigma1) >>> 0;
    }

    let a = hash[0];
    let b = hash[1];
    let c = hash[2];
    let d = hash[3];
    let e = hash[4];
    let f = hash[5];
    let g = hash[6];
    let h = hash[7];

    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);

      const choice = (e & f) ^ (~e & g);

      const temp1 = (h + sum1 + choice + constants[index] + words[index]) >>> 0;

      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);

      const majority = (a & b) ^ (a & c) ^ (b & c);

      const temp2 = (sum0 + majority) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    hash[0] = (hash[0] + a) >>> 0;
    hash[1] = (hash[1] + b) >>> 0;
    hash[2] = (hash[2] + c) >>> 0;
    hash[3] = (hash[3] + d) >>> 0;
    hash[4] = (hash[4] + e) >>> 0;
    hash[5] = (hash[5] + f) >>> 0;
    hash[6] = (hash[6] + g) >>> 0;
    hash[7] = (hash[7] + h) >>> 0;
  }

  return hash.map((word) => word.toString(16).padStart(8, "0")).join("");
}

function cyanRandomUuid(): string {
  if (
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.randomUUID === "function"
  ) {
    return globalThis.crypto.randomUUID();
  }

  if (
    typeof globalThis.crypto === "undefined" ||
    typeof globalThis.crypto.getRandomValues !== "function"
  ) {
    throw new Error("Secure browser randomness is unavailable.");
  }

  const bytes = new Uint8Array(16);

  globalThis.crypto.getRandomValues(bytes);

  bytes[6] = (bytes[6] & 0x0f) | 0x40;

  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}

export interface CyanDurableOwnerAuthorityInput extends CyanOwnerAuthorityInput {
  /**
   * Stable governed scope:
   * project / repository / environment / capability family.
   */
  scope: string;
}

function normalized(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

export function cyanOwnerDecisionFingerprint(
  input: CyanDurableOwnerAuthorityInput,
): string {
  const semanticAuthorityState = {
    action: normalized(input.action),
    scope: normalized(input.scope),
    risk: input.risk,
    reversible: input.reversible,
    governedScope: input.governedScope,

    destroysSs: Boolean(input.destroysSs),
    destroysExternalProject: Boolean(input.destroysExternalProject),
    destroysCriticalProductionData: Boolean(
      input.destroysCriticalProductionData,
    ),
    revokesOwnerAccess: Boolean(input.revokesOwnerAccess),
    exposesSecrets: Boolean(input.exposesSecrets),
    crossesUnknownAuthorityBoundary: Boolean(
      input.crossesUnknownAuthorityBoundary,
    ),
  };

  return cyanSha256(JSON.stringify(semanticAuthorityState));
}

export interface CyanDurableOwnerAuthorityResult extends CyanOwnerAuthorityResult {
  fingerprint: string;

  source: "DURABLE_AUTHORITY_REUSE" | "CYAN_OWNER_DECISION";

  persisted: boolean;
}

export class CyanOwnerAuthorityService {
  constructor(private readonly repository: CyanOwnerDecisionRepository) {}

  async evaluate(
    input: CyanDurableOwnerAuthorityInput,
  ): Promise<CyanDurableOwnerAuthorityResult> {
    const fingerprint = cyanOwnerDecisionFingerprint(input);

    const existing = await this.repository.findByFingerprint(fingerprint);

    if (existing && existing.reusable) {
      return {
        decision: existing.decision,

        authorized: existing.authorized,

        requiresHumanOwner: existing.requiresHumanOwner,

        decidedBy: existing.decidedBy,

        persistDecision: true,

        reason: `Durable Cyan owner decision reused. ${existing.reason}`,

        fingerprint,

        source: "DURABLE_AUTHORITY_REUSE",

        persisted: true,
      };
    }

    const result = evaluateCyanOwnerAuthority({
      ...input,

      authorizationExists: false,
    });

    const now = new Date().toISOString();

    const record: CyanOwnerDecisionRecord = {
      id: cyanRandomUuid(),

      fingerprint,

      action: input.action,

      scope: input.scope,

      risk: input.risk,

      decision: result.decision,

      authorized: result.authorized,

      requiresHumanOwner: result.requiresHumanOwner,

      decidedBy: result.decidedBy,

      reason: result.reason,

      /*
       * Autonomous approvals/denials are reusable.
       *
       * Human break-glass requirements are also persisted as knowledge
       * so Cyan does not repeatedly re-discover the same prohibited
       * boundary. Persistence does NOT convert them into authorization.
       */
      reusable: true,

      createdAt: now,

      updatedAt: now,
    };

    await this.repository.save(record);

    return {
      ...result,

      fingerprint,

      source: "CYAN_OWNER_DECISION",

      persisted: true,
    };
  }
}
